'use client';

import Image from 'next/image';
import { type JSX, useState } from 'react';

import type { DesktopNavItemConfig } from '../types';
import LayeredMenu from './LayeredMenu';
import SingleColumnMenu from './SingleColumnMenu';
import TwoColumnMenu from './TwoColumnMenu';

const AI_GRADIENT_BG = 'linear-gradient(to right, #9952E0 0%, #FF66E6 100%)';

interface DesktopNavItemProps {
    config: DesktopNavItemConfig;
    darkHeader?: boolean;
}

export default function DesktopNavItem({ config, darkHeader = false }: DesktopNavItemProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const { dropdown, hasGradientText, gradientIcon } = config;

    // Plain link — no dropdown
    if (!dropdown) {
        return (
            <a
                href={config.href}
                className={`flex h-10 items-center gap-1 rounded-md px-3 text-base font-semibold ${
                    darkHeader
                        ? 'text-white hover:bg-gray-700 hover:text-blue-400'
                        : 'text-gray-900 hover:bg-blue-100 hover:text-blue-600'
                }`}
            >
                {config.label}
            </a>
        );
    }

    return (
        <div
            className="relative"
            onMouseEnter={(): void => setIsOpen(true)}
            onMouseLeave={(): void => setIsOpen(false)}
        >
            {/* Trigger pill */}
            <button
                type="button"
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-base font-semibold transition-colors ${
                    isOpen
                        ? hasGradientText
                            ? 'text-white'
                            : darkHeader
                              ? 'bg-gray-700 text-blue-400'
                              : 'bg-blue-100 text-blue-600'
                        : darkHeader
                          ? 'text-white'
                          : ''
                }`}
                style={isOpen && hasGradientText ? { background: AI_GRADIENT_BG } : undefined}
            >
                {/* Label */}
                {hasGradientText && !isOpen ? (
                    <span className="flex items-center gap-1">
                        {gradientIcon && <Image src={gradientIcon} alt="" width={9} height={9} />}
                        <span
                            style={{
                                background: AI_GRADIENT_BG,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                            }}
                        >
                            {config.label}
                        </span>
                    </span>
                ) : hasGradientText && isOpen ? (
                    <span className="flex items-center gap-1">
                        {gradientIcon && (
                            <Image src="/assets/images/starFourSidedGradientWhite.svg" alt="" width={9} height={9} />
                        )}
                        <span>{config.label}</span>
                    </span>
                ) : (
                    <span>{config.label}</span>
                )}

                {/* Chevron */}
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                >
                    <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <>
                    {/* Spacer for mouse bridge */}
                    <div className="absolute left-0 right-0 z-40 h-7" />

                    {/* Dropdown shell */}
                    <div className="absolute left-0 top-full z-50 mt-7 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
                        {dropdown.type === 'two-column' && (
                            <TwoColumnMenu links={dropdown.links} cta={dropdown.cta} categories={dropdown.categories} />
                        )}
                        {dropdown.type === 'single-column' && <SingleColumnMenu links={dropdown.links} />}
                        {dropdown.type === 'layered' && <LayeredMenu sections={dropdown.sections} />}
                    </div>
                </>
            )}
        </div>
    );
}
